import type { ContactOrderCreatePayload } from "../../../../../../../../../../../../api/kehrblatt/contact-orders"
import {
  buildWorkOrderById,
  toIsoString,
  validateAndBuildPayload,
  type ScheduleEventFormState,
} from "../../../../../../../../../../../../_shared/_logic/form-state"
import type {
  AppointmentAnnouncementOutput,
  AppointmentCreateInit,
  AppointmentGeneralOutput,
  AppointmentPlanningOutput,
} from "../../../../../../../_logic/create-appointment-contract"
import { normalizeId } from "../../../../../_shared/_logic/work-order-draft"

export const resolveAppointmentWorkOrder = ({
  init,
  general,
}: {
  init: AppointmentCreateInit
  general: AppointmentGeneralOutput
}) => {
  const selected = general.selectedWorkOrder?.workOrder ?? null

  if (selected && normalizeId(selected.id) === normalizeId(general.workOrderId)) return selected

  return buildWorkOrderById(init.workOrders)[general.workOrderId] ?? null
}

const resolveAppointmentCandidateEmployeeIds = ({
  init,
  general,
}: {
  init: AppointmentCreateInit
  general: AppointmentGeneralOutput
}) => {
  const workOrder = resolveAppointmentWorkOrder({ init, general })
  const allowed = Array.isArray(workOrder?.allowed_employee_ids)
    ? workOrder.allowed_employee_ids.filter(Boolean)
    : []

  if (!allowed.length && general.workOrderSource === "new") {
    const draftAllowed = general.newWorkOrderDraft?.allowed_employee_ids?.filter(Boolean) ?? []

    if (draftAllowed.length) return draftAllowed
  }

  if (general.workOrderSource === "existing" && !allowed.length) {
    return init.employees.map((employee) => employee.id).filter(Boolean)
  }

  return allowed.length ? allowed : init.candidateEmployeeIds
}

export const buildAppointmentFormState = ({
  init,
  general,
  planning,
  workOrderIdOverride,
}: {
  init: AppointmentCreateInit
  general: AppointmentGeneralOutput
  planning: AppointmentPlanningOutput
  workOrderIdOverride?: string | null
}): ScheduleEventFormState => {
  const workOrder = resolveAppointmentWorkOrder({ init, general })
  const draft = general.workOrderSource === "new" ? general.newWorkOrderDraft : null

  return {
    employee_id: planning.employeeId ?? "",
    event_type: "APPOINTMENT",
    flex_type: planning.flexType,
    status: planning.status,
    start_at: planning.startAt,
    end_at: planning.endAt,
    timeframe_start_at: planning.timeframeStartAt,
    timeframe_end_at: planning.timeframeEndAt,
    all_day: false,
    title: "",
    description: "",
    customer_note: "",
    location_text: "",
    property_id: workOrder?.property_id ?? draft?.property_id ?? init.defaultPropertyId ?? "",
    work_order_id: workOrderIdOverride ?? general.workOrderId,
  }
}

export const buildAppointmentCreatePayload = ({
  init,
  general,
  planning,
  now,
  workOrderIdOverride,
}: {
  init: AppointmentCreateInit
  general: AppointmentGeneralOutput
  planning: AppointmentPlanningOutput
  now: Date
  workOrderIdOverride?: string | null
}) => {
  const form = buildAppointmentFormState({ init, general, planning, workOrderIdOverride })

  return validateAndBuildPayload({
    form,
    event: null,
    defaultStatus: init.defaultStatus,
    defaultFlexType: init.defaultFlexType,
    isAppointment: true,
    startIso: toIsoString(planning.startAt),
    endIso: toIsoString(planning.endAt),
    startDate: planning.startAt ? new Date(planning.startAt) : null,
    endDate: planning.endAt ? new Date(planning.endAt) : null,
    now,
    workingHoursByUser: init.workingHoursByUser,
    scheduleEvents: init.scheduleEvents,
    candidateEmployeeIds: resolveAppointmentCandidateEmployeeIds({ init, general }),
    organizationId: init.organizationId,
  })
}

export const resolveAppointmentContactOrderAssignedEmployeeIds = ({
  init,
  general,
  event,
}: {
  init: AppointmentCreateInit
  general: AppointmentGeneralOutput
  event: ScheduleEvent
}): string[] => {
  const workOrder = resolveAppointmentWorkOrder({ init, general })
  const workOrderAllowedEmployeeIds = Array.isArray(workOrder?.allowed_employee_ids)
    ? workOrder.allowed_employee_ids.filter(Boolean)
    : []

  if (!workOrderAllowedEmployeeIds.length && general.workOrderSource === "new") {
    const draftAllowed = general.newWorkOrderDraft?.allowed_employee_ids?.filter(Boolean) ?? []

    if (draftAllowed.length) return Array.from(new Set(draftAllowed))
  }

  if (workOrderAllowedEmployeeIds.length) return Array.from(new Set(workOrderAllowedEmployeeIds))

  if (workOrder?.assigned_employee_id) return [workOrder.assigned_employee_id]

  if (event.employee_id) return [event.employee_id]

  return []
}

export const buildAppointmentContactOrderPayload = ({
  init,
  event,
  general,
  planning,
  announcement,
}: {
  init: AppointmentCreateInit
  event: ScheduleEvent
  general: AppointmentGeneralOutput
  planning: AppointmentPlanningOutput
  announcement: AppointmentAnnouncementOutput
}): ContactOrderCreatePayload => ({
  work_order_id: general.workOrderId,
  schedule_event_id: event.id,
  replace_existing: false,
  assigned_employee_ids: resolveAppointmentContactOrderAssignedEmployeeIds({
    init,
    general,
    event,
  }),
  proposed_start_at: toIsoString(planning.startAt),
  proposed_end_at: toIsoString(planning.endAt),
})
